import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../shared/types/domain";
import type { UserSettings } from "../shared/types/domain";
import { reportOnboardingView } from "../shared/lib/analytics";

interface OnboardingViewProps {
  newBabyName: string;
  setNewBabyName: (v: string) => void;
  newBabyBirthDate: string;
  setNewBabyBirthDate: (v: string) => void;
  newBabySex: "boy" | "girl" | "";
  setNewBabySex: (v: "boy" | "girl" | "") => void;
  newBabyFeedingMode: "bottle" | "nursing" | "both";
  setNewBabyFeedingMode: (v: "bottle" | "nursing" | "both") => void;
  language: string;
  saveUserSettings: (patch: Partial<UserSettings>) => void;
  onboardingSubmitAttempted: boolean;
  onboardingMissingFields: string[];
  onSubmit: () => void;
  isAnonymous: boolean;
  onRequestSignIn: () => void;
  onSignOut: () => void;
}

export function OnboardingView({
  newBabyName, setNewBabyName, newBabyBirthDate, setNewBabyBirthDate,
  newBabySex, setNewBabySex, newBabyFeedingMode, setNewBabyFeedingMode,
  language, saveUserSettings,
  onboardingSubmitAttempted, onboardingMissingFields, onSubmit,
  isAnonymous, onRequestSignIn, onSignOut,
}: OnboardingViewProps) {
  const { t } = useTranslation();

  useEffect(() => {
    reportOnboardingView();
  }, []);

  return (
    <main className="screen screen--setup">
      <h1 className="setup-title">{t("onboarding.title")}</h1>
      <p className="setup-subtitle">{t("onboarding.subtitle")}</p>
      <ul className="setup-features" aria-label={t("onboarding.featuresLabel")}>
        <li>{t("onboarding.features.feed")}</li>
        <li>{t("onboarding.features.insights")}</li>
        <li>{t("onboarding.features.free")}</li>
      </ul>
      <div className="setup-section">
        <p className="setup-section-label">{t("onboarding.sectionBaby")}</p>
        <div className="form-block">
          <input placeholder={t("onboarding.babyNamePlaceholder")} value={newBabyName} onChange={(e) => setNewBabyName(e.target.value)} />
          <label className="setup-field-label">
            {t("onboarding.dateOfBirth")}
            <input type="date" value={newBabyBirthDate} onChange={(e) => setNewBabyBirthDate(e.target.value)} />
          </label>
          <label className="setup-field-label">{t("onboarding.sexOptional")}</label>
          <div className="diaper-row">
            {(["", "boy", "girl"] as const).map((s) => (
              <button key={s} type="button" className={`diaper-btn${newBabySex === s ? " selected" : ""}`} onClick={() => setNewBabySex(s)}>
                {s === "" ? t("onboarding.skip") : s === "boy" ? t("onboarding.boy") : t("onboarding.girl")}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="setup-section">
        <p className="setup-section-label">{t("onboarding.sectionFeeding")}</p>
        <div className="diaper-row">
          {(["bottle", "nursing", "both"] as const).map((m) => (
            <button key={m} type="button" className={`diaper-btn${newBabyFeedingMode === m ? " selected" : ""}`} onClick={() => setNewBabyFeedingMode(m)}>
              {m === "bottle" ? t("feeding.bottle") : m === "nursing" ? t("feeding.nursing") : t("feeding.both")}
            </button>
          ))}
        </div>
      </div>
      <div className="setup-section">
        <p className="setup-section-label">{t("onboarding.sectionPreferences")}</p>
        <label className="setup-field-label">
          {t("onboarding.appLanguage")}
          <select className="country-select" value={language} onChange={(e) => saveUserSettings({ language: e.target.value })}>
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </label>
      </div>
      <button
        className="success-btn"
        style={{ marginTop: 8 }}
        onClick={onSubmit}
      >
        {t("onboarding.createProfile")}
      </button>
      {onboardingSubmitAttempted && onboardingMissingFields.length > 0 && (
        <div role="alert" className="auth-error">
          {onboardingMissingFields.map((field) => (
            <p key={field} style={{ margin: "0.2rem 0" }}>{t("onboarding.fieldRequired", { field })}</p>
          ))}
        </div>
      )}
      {isAnonymous ? (
        <button type="button" className="ghost-btn setup-signout" onClick={onRequestSignIn}>
          {t("auth.alreadyHaveAccount")}
        </button>
      ) : (
        <button className="ghost-btn setup-signout" onClick={onSignOut}>
          {t("common.signOut")}
        </button>
      )}
      <a href="/about/" className="about-link-btn" style={{ display: 'block', textAlign: 'center', marginTop: '0.75rem' }}>
        {t("auth.whatIsBabyLog")}
      </a>
      <a href="/blog/" className="about-link-btn" style={{ display: 'block', textAlign: 'center', marginTop: '0.25rem' }}>
        {t("auth.readBlog")}
      </a>
    </main>
  );
}
